/**
 * OptiHubPage — central hub for staff tools/workflows. Gated to the console tier
 * (CONSOLE_ROLES — root/engineer/support/readonly); the `member` role can't reach
 * it (sidebar link hidden + route 403). Backend enforces too.
 *
 * Page chrome mirrors CustomersPage (padded column, title + subtitle). Content
 * is organized into tabs; the first is the Sales Console (trial/plan tools).
 */
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@staff-app/ui";
import { SalesConsole } from "../components/SalesConsole";
import { AccountsConsole } from "../components/AccountsConsole";
import { BillingConsole } from "../components/BillingConsole";
import { CouponCodeConsole } from "../components/CouponCodeConsole";
import { PowerBiAuthConsole } from "../components/PowerBiAuthConsole";
import { BatchOnboardingConsole } from "../components/BatchOnboardingConsole";
import { AccountAuditLogConsole } from "@/features/account-audit-log/components/AccountAuditLogConsole";
import { LayoutGrid } from "lucide-react";
import { SECTION_TAB_LIST, SECTION_TAB_RADIX } from "@/lib/section-tabs";
import { ADMIN_ROLES } from "@staff-app/shared-types";
import { useAuthStore } from "@/stores/auth-store";

export function OptiHubPage() {
  /**
   * Early Promoters Coupon is ROOT-only, while this whole route is root+support — so the tab is
   * hidden rather than the page. Hiding it is a courtesy, not the control: every op behind
   * it carries `@RequireRoles(AdminRole.ROOT)` on the resolver, because the mutation is
   * reachable without the tab.
   */
  const me = useAuthStore((s) => s.adminUser);
  const isRoot = !!me?.roles?.includes(ADMIN_ROLES.ROOT);
  // BILLING reaches OptiHub for ONE thing — the Billing Console's Dunning control
  // (requester, 2026-09-23). A user who is billing and neither root nor support gets that
  // alone: no other OptiHub tab, and the console in its dunning-only mode.
  const billingOnly =
    !!me?.roles?.includes(ADMIN_ROLES.BILLING) &&
    !isRoot &&
    !me?.roles?.includes(ADMIN_ROLES.SUPPORT);
  // Account Audit Log — ROOT + SUPPORT (requester, 2026-10-01). Everyone past the billingOnly
  // branch below is one of the two today; the explicit check keeps a future OptiHub role from
  // inheriting a tab whose queries would refuse it.
  const canSeeAuditLog = isRoot || !!me?.roles?.includes(ADMIN_ROLES.SUPPORT);

  if (billingOnly) {
    return (
      <div className="flex h-full flex-col overflow-hidden" data-testid="optihub-page">
        <div className="px-6 pt-6">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
            <LayoutGrid className="h-6 w-6" />
            {"Billing Console"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {"Look up a customer by Stripe ID to stop, pause or resume their dunning."}
          </p>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-6">
          <BillingConsole dunningOnly />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-full flex-col overflow-hidden"
      data-testid="optihub-page"
    >
      <div className="px-6 pt-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <LayoutGrid className="h-6 w-6" />
          {"OptiHub"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {"Central hub for OptiSigns staff tools and workflows."}
        </p>
      </div>

      <Tabs
        defaultValue="sales-console"
        className="flex min-h-0 flex-1 flex-col"
      >
        <TabsList className={`mt-4 ${SECTION_TAB_LIST}`}>
          <TabsTrigger
            value="sales-console"
            className={SECTION_TAB_RADIX}
            data-testid="optihub-tab-sales-console"
          >
            {"Sales Console"}
          </TabsTrigger>
          <TabsTrigger
            value="accounts-console"
            className={SECTION_TAB_RADIX}
            data-testid="optihub-tab-accounts-console"
          >
            {"Accounts Console"}
          </TabsTrigger>
          <TabsTrigger
            value="billing-console"
            className={SECTION_TAB_RADIX}
            data-testid="optihub-tab-billing-console"
          >
            {"Billing Console"}
          </TabsTrigger>
          {canSeeAuditLog ? (
            <TabsTrigger
              value="account-audit-log"
              className={SECTION_TAB_RADIX}
              data-testid="optihub-tab-account-audit-log"
            >
              {"Account Audit Log"}
            </TabsTrigger>
          ) : null}
          {isRoot ? (
            <TabsTrigger
              value="apply-coupon-code"
              className={SECTION_TAB_RADIX}
              data-testid="optihub-tab-apply-coupon-code"
            >
              {"Early Promoters Coupon"}
            </TabsTrigger>
          ) : null}
          <TabsTrigger
            value="powerbi-auth"
            className={SECTION_TAB_RADIX}
            data-testid="optihub-tab-powerbi-auth"
          >
            {"Power BI Auth"}
          </TabsTrigger>
          <TabsTrigger
            value="batch-onboarding"
            className={SECTION_TAB_RADIX}
            data-testid="optihub-tab-batch-onboarding"
          >
            {"Batch Onboarding"}
          </TabsTrigger>
        </TabsList>
        <div className="min-h-0 flex-1 overflow-auto p-6">
          <TabsContent value="sales-console">
            <SalesConsole />
          </TabsContent>
          <TabsContent value="accounts-console">
            <AccountsConsole />
          </TabsContent>
          <TabsContent value="billing-console">
            <BillingConsole />
          </TabsContent>
          {canSeeAuditLog ? (
            <TabsContent value="account-audit-log">
              <AccountAuditLogConsole />
            </TabsContent>
          ) : null}
          {isRoot ? (
            <TabsContent value="apply-coupon-code">
              <CouponCodeConsole />
            </TabsContent>
          ) : null}
          <TabsContent value="powerbi-auth">
            <PowerBiAuthConsole />
          </TabsContent>
          <TabsContent value="batch-onboarding">
            <BatchOnboardingConsole />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
